import moment from "moment";

/**
 * Date math for Sequence Builder sequences (PS-5765). Kept free of DB and
 * timezone imports so the admin UI can show the same schedule the server
 * will write.
 */

const SATURDAY = 6;
const SUNDAY = 0;

export const SEQUENCE_DATE_FORMAT = "YYYY-MM-DD";

export function isWeekendDay(day: number): boolean {
  return day === SATURDAY || day === SUNDAY;
}

/** Moves a Saturday or Sunday forward to the Monday; weekdays are unchanged. */
export function toWeekday(date: moment.Moment): moment.Moment {
  const result = date.clone();
  while (isWeekendDay(result.day())) {
    result.add(1, "day");
  }
  return result;
}

/**
 * Calendar dates ("YYYY-MM-DD") for each DM of a sequence. DM1 goes on the
 * start date and each later DM `cadenceDays` after the one before it. Any DM
 * that lands on a weekend moves to the Monday, and the next DM counts from
 * that Monday, so two DMs never collapse onto the same day.
 */
export function computeSequenceSendDates(
  startDate: string,
  dmCount: number,
  cadenceDays: number,
): string[] {
  const dates: string[] = [];
  let current = toWeekday(moment(startDate, SEQUENCE_DATE_FORMAT, true));
  for (let i = 0; i < dmCount; i++) {
    if (i > 0) {
      current = toWeekday(current.clone().add(cadenceDays, "days"));
    }
    dates.push(current.format(SEQUENCE_DATE_FORMAT));
  }
  return dates;
}

/**
 * The earliest date an admin can pick to start or reschedule a sequence:
 * yesterday in UTC, which is still today in the timezones furthest behind
 * UTC. The server doesn't know the admin's timezone, so any date that is
 * today somewhere is allowed; enrollment and rescheduling then move anyone
 * whose own day or send time has already passed on to their next one.
 */
export function earliestPickableDate(now: Date = new Date()): string {
  return moment.utc(now).subtract(1, "day").format(SEQUENCE_DATE_FORMAT);
}

/** A real calendar date ("2026-02-30" isn't) that isn't past everywhere. */
export function isPickableDate(date: string, now: Date = new Date()): boolean {
  return (
    typeof date === "string" &&
    moment(date, SEQUENCE_DATE_FORMAT, true).isValid() &&
    date >= earliestPickableDate(now)
  );
}

/**
 * Moves every date by `days`, keeping the gaps: a date that lands on a
 * weekend goes to the Monday, and no DM shares a day with the one before.
 */
export function shiftSequenceDates(dates: string[], days: number): string[] {
  const shifted: string[] = [];
  let previous: moment.Moment | null = null;
  for (const date of dates) {
    let current = toWeekday(
      moment(date, SEQUENCE_DATE_FORMAT, true).add(days, "days"),
    );
    while (previous && !current.isAfter(previous, "day")) {
      current = toWeekday(current.add(1, "day"));
    }
    shifted.push(current.format(SEQUENCE_DATE_FORMAT));
    previous = current;
  }
  return shifted;
}

/**
 * Checks dates an admin set for each DM by hand (PS-5851, e.g. to skip a
 * holiday): one per DM, each a pickable weekday, each after the one before.
 * Returns what to fix, or null.
 */
export function sequenceDatesError(
  dates: unknown,
  dmCount: number,
  now: Date = new Date(),
): string | null {
  if (!Array.isArray(dates) || dates.length !== dmCount) {
    return `Pick a date for each of the ${dmCount} DMs.`;
  }
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    if (!isPickableDate(date, now)) {
      return `DM${i + 1}'s date must be a real date, today or later.`;
    }
    if (isWeekendDay(moment(date, SEQUENCE_DATE_FORMAT, true).day())) {
      return `DM${i + 1} falls on a weekend; pick a weekday.`;
    }
    if (i > 0 && date <= dates[i - 1]) {
      return `DM${i + 1} must be after DM${i}.`;
    }
  }
  return null;
}
